import type { MultiValue, Option } from '@ksyos/design-system';
import { DateTime } from '@ksyos/date-time';
import { Button, Card, FormButtonRow, FormGroup, FormParagraph, PageHeader, Select } from '@ksyos/design-system';
import { Fragment, useEffect, useState } from 'react';
import type { IEpisodeCategory } from '@common/interfaces';
import { DebtorType, ManagerAuthorization } from '@common/enums';
import { formatString } from '@common/utils/formatString';
import { assertHasAuthorization, useDownloadFile } from '@frontend/utils';
import type { DeclarationBatchInformation } from '../../../../common/utils/declarationBatchInformation';
import type { ValidationErrors } from '../../../utils/validation';
import type { IInsurer } from '../../interfaces/insurer';
import type { IService } from '../../interfaces/service';
import { BatchType } from '../../../../common/enums/batchType';
import { EpisodeCategory } from '../../../../common/enums/episodeCategory';
import { declarationsExportSchema } from '../../../../common/validation/declarationsExport';
import { getSelectedLanguage } from '../../../translate';
import { capitalizeFirstLetter } from '../../../utils/capitalizeFirstLetter';
import { createEnumeration } from '../../../utils/enumeration';
import { useAbortableFetch } from '../../../utils/hooks/useAbortableFetch';
import { emptyValidationErrors, validate } from '../../../utils/validation';
import { BatchesTable } from '../../elements/BatchesTable';
import { DateInput, DropdownSearch, Feedback, InputCheckbox } from '../../elements/common';
import { ConfirmationModal } from '../../elements/modals/ConfirmationModal';
import { PageTemplate } from '../../templates/PageTemplate';

export function DeclarationsExport() {
    const abortableFetch = useAbortableFetch();
    const [selectedEpisodeCategoryName, setSelectedEpisodeCategoryName] = useState<EpisodeCategory | null>(null);
    const [selectedEpisodeCategory, setSelectedEpisodeCategory] = useState<Option | null>(null);
    const [selectedServices, setSelectedServices] = useState<MultiValue<Option>>([]);
    const [selectedInsurer, setSelectedInsurer] = useState<Option | null>(null);
    const [services, setServices] = useState<Option[]>([]);
    const [episodeCategories, setEpisodeCategories] = useState<IEpisodeCategory[]>([]);
    const [compositeFundingEpisodeCategories, setCompositeFundingEpisodeCategories] = useState<IEpisodeCategory[]>([]);
    const [episodeCategoryOptions, setEpisodeCategoryOptions] = useState<Option[]>([]);
    const [isPreview, setIsPreview] = useState(false);
    const [startedBeforeDate, setStartedBeforeDate] = useState<string | null>(null);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [count, setCount] = useState(0);
    const { downloadFile } = useDownloadFile();

    const translate = getSelectedLanguage();

    assertHasAuthorization(ManagerAuthorization.exportDeclarationRecords);

    useEffect(() => {
        if (selectedEpisodeCategory) {
            setSelectedServices([]);
            loadServices(selectedEpisodeCategory.value);

            setSelectedEpisodeCategoryName(
                episodeCategories.find((o) => o.id === selectedEpisodeCategory.value)?.name ?? null
            );
        }
    }, [selectedEpisodeCategory]);

    useEffect(() => {
        if (errors.size) {
            const validationErrors = validateDeclarationsExportForm();
            setErrors(validationErrors);
        }
    }, [selectedEpisodeCategory, selectedServices, selectedInsurer, startedBeforeDate]);

    useEffect(() => {
        loadEpisodeCategories();
    }, []);

    return (
        <PageTemplate width="medium">
            <PageHeader title={translate.finance.insurerDeclarations} />
            <Card>
                <Card.Header
                    title={translate.export.declarationsProcessExport}
                    tooltipContent={translate.export.declarationsProcessExportText}
                />
                <Card.Body>
                    <FormGroup
                        hasErrors={errors.has('startedBeforeDate')}
                        isRequired={true}
                        label={capitalizeFirstLetter(translate.common.upToAndIncluding)}
                    >
                        <DateInput
                            error={errors.get('startedBeforeDate')}
                            hasMaxWidth={true}
                            maxDate={DateTime.now().startOf('day').toISOString()}
                            onChange={setStartedBeforeDate}
                            placeholder={translate.common.dateFormat}
                            size="large"
                            value={startedBeforeDate}
                            variant="outline"
                        />
                    </FormGroup>

                    <FormGroup
                        hasErrors={errors.has('episodeCategory')}
                        isRequired={true}
                        label={translate.episodeCategory.episodeCategory}
                    >
                        <Select
                            closeMenuOnSelect={false}
                            isClearable={false}
                            isMulti={false}
                            onChange={setSelectedEpisodeCategory}
                            options={episodeCategoryOptions}
                            value={selectedEpisodeCategory}
                        />

                        <Feedback
                            error={errors.get('episodeCategory')}
                            key="feedback"
                        />
                    </FormGroup>

                    <FormGroup
                        hasErrors={errors.has('serviceIds')}
                        isRequired={Boolean(
                            selectedEpisodeCategoryName &&
                                isCompositeFundingEpisodeCategory(selectedEpisodeCategoryName) === false
                        )}
                        label={translate.export.servicesForDeclaration}
                    >
                        {selectedEpisodeCategoryName &&
                        isCompositeFundingEpisodeCategory(selectedEpisodeCategoryName) ? (
                            <FormParagraph>
                                {formatString(translate.export.episodeCategoryHasFixedServices, [
                                    translate.episodeCategoryEnum[selectedEpisodeCategoryName],
                                ])}
                            </FormParagraph>
                        ) : (
                            <>
                                <Select
                                    closeMenuOnSelect={false}
                                    isClearable={true}
                                    isMulti={true}
                                    noOptionsMessage={() => translate.common.typeToSearch}
                                    onChange={setSelectedServices}
                                    options={services}
                                    placeholder={translate.export.selectOneOrMoreService}
                                    value={selectedServices}
                                />

                                <Feedback
                                    error={errors.get('serviceIds')}
                                    key="feedback"
                                />
                            </>
                        )}
                    </FormGroup>

                    <FormGroup
                        hasErrors={errors.has('insurerId')}
                        label={translate.common.healthInsurer}
                    >
                        <DropdownSearch
                            clearable={true}
                            error={errors.get('insurer')}
                            loadOptions={loadInsurers}
                            onChange={(value, label) => {
                                if (value && label) {
                                    setSelectedInsurer({ label, value });
                                } else {
                                    setSelectedInsurer(null);
                                }
                            }}
                            placeholder={`${translate.common.search}...`}
                            value={selectedInsurer?.value}
                        />
                    </FormGroup>

                    <FormButtonRow>
                        <InputCheckbox
                            checked={isPreview}
                            name="isPreview"
                            onChange={setIsPreview}
                            text="Proefexport"
                        />
                        <Button onClick={async () => await downloadExport()}>{translate.export.export}</Button>
                    </FormButtonRow>
                </Card.Body>
            </Card>

            <Card>
                <Fragment>
                    <BatchesTable<DeclarationBatchInformation>
                        batchType={BatchType.declaration}
                        informationColumns={[
                            {
                                key: 'startedBeforeDate',
                                label: translate.common.startedAt,
                            },
                            {
                                key: 'episodeCategory',
                                label: translate.episodeCategory.episodeCategory,
                            },
                            {
                                key: 'serviceName',
                                label: translate.service.service,
                            },
                            {
                                key: 'insurerName',
                                label: translate.common.healthInsurer,
                            },
                        ]}
                        onExport={downloadExport}
                        title={translate.export.declarationsExport}
                    ></BatchesTable>
                </Fragment>
            </Card>

            <ConfirmationModal
                handleNo={() => setShowConfirmationModal(false)}
                handleYes={async () => {
                    setShowConfirmationModal(false);
                    downloadFile(fetchDeclarationsExport);
                }}
                isOpen={showConfirmationModal}
                question={getConfirmationModalMessage()}
                title={translate.common.notice}
            />
        </PageTemplate>
    );

    function createValidationObject(batchId?: string) {
        return {
            batchId: batchId ?? null,
            episodeCategory: selectedEpisodeCategoryName,
            episodeCategoryId: selectedEpisodeCategory?.value ?? null,
            insurerId: selectedInsurer?.value ?? null,
            isPreview,
            serviceIds: selectedServices.map((selectedService) => selectedService.value),
            startedBeforeDate,
        };
    }

    async function loadEpisodeCategories() {
        const [allEpisodeCategoriesResult, compositeFundingEpisodeCategoriesResult] = await Promise.all([
            getAllEpisodeCategories(),
            getCompositeFundingEpisodeCategories(),
        ]);

        setEpisodeCategories(allEpisodeCategoriesResult);

        const options = allEpisodeCategoriesResult
            .map((episodeCategory) => {
                return { label: translate.episodeCategoryEnum[episodeCategory.name], value: episodeCategory.id };
            })
            .sort((a, b) => a.label.localeCompare(b.label));

        setEpisodeCategoryOptions(options);

        const somnologyEpisodeCategory = allEpisodeCategoriesResult.find((ec) => ec.name === EpisodeCategory.somnology);
        if (somnologyEpisodeCategory) {
            setCompositeFundingEpisodeCategories([
                ...compositeFundingEpisodeCategoriesResult,
                somnologyEpisodeCategory,
            ]);
        } else {
            setCompositeFundingEpisodeCategories(compositeFundingEpisodeCategoriesResult);
        }
    }

    async function getAllEpisodeCategories(): Promise<IEpisodeCategory[]> {
        const url = `/api/episodeCategories`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        return result.json.items || [];
    }

    async function getCompositeFundingEpisodeCategories(): Promise<IEpisodeCategory[]> {
        const url = `/api/episodeCategories?onlyCompositeFunding=true&debtorType=${DebtorType.insurer}`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        return result.json.items || [];
    }

    async function loadServices(episodeCategoryId: string) {
        const url = `/api/services?selectionList=true&episodeCategoryId=${episodeCategoryId}`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        if (result.json.items) {
            const options = result.json.items.map((service: IService) => {
                return { label: service.name, value: service.id };
            });

            setServices(options);
        } else {
            setServices([]);
        }
    }

    async function loadInsurers(searchText: string) {
        let options = [];
        const url = `/api/insurers${searchText ? `?searchText=${searchText}` : ''}`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        if (result.json.items) {
            options = result.json.items.map((item: IInsurer) => {
                return { label: item.name, value: item.id };
            });
        }

        return options;
    }

    function fetchDeclarationsExport(batchId?: string) {
        return abortableFetch.fetch('/api/finance/declarations/export', 'POST', getExportRequestBody(batchId));
    }

    function fetchDeclarationsExportCount() {
        return abortableFetch.fetchJson('/api/finance/declarations/export/count', 'POST', getExportRequestBody());
    }

    function getExportRequestBody(batchId?: string) {
        return {
            batchId,
            episodeCategory: selectedEpisodeCategoryName,
            episodeCategoryId: selectedEpisodeCategory?.value,
            insurerId: selectedInsurer?.value,
            insurerName: selectedInsurer?.label,
            isPreview,
            serviceIds: selectedServices.map((selectedService) => selectedService.value),
            serviceNames: selectedServices.map((selectedService) => selectedService.label),
            startedBeforeDate,
        };
    }

    function validateDeclarationsExportForm(batchId?: string) {
        const { errors: validationErrors } = validate(declarationsExportSchema, createValidationObject(batchId));

        return validationErrors;
    }

    async function downloadExport(batchId?: string) {
        const validationErrors = validateDeclarationsExportForm(batchId);
        setErrors(validationErrors);

        if (validationErrors.size > 0) {
            return;
        }

        if (batchId) {
            await downloadFile(() => fetchDeclarationsExport(batchId));
        } else {
            const result = await fetchDeclarationsExportCount();

            setCount(result.json.count);
            setShowConfirmationModal(true);
        }
    }

    function getConfirmationModalMessage() {
        if (!startedBeforeDate) {
            return '';
        }

        let message = `Weet je zeker dat je ${count} declaratieregels tot en met ${DateTime.from(
            startedBeforeDate
        ).format('DD-MM-YYYY')}`;

        if (selectedServices.length > 0) {
            const serviceNames = selectedServices.map(
                (selectedService) => services.find((service) => service.value === selectedService.value)?.label ?? ''
            );
            message += `, voor ${createEnumeration(serviceNames)}`;
        }

        if (selectedEpisodeCategoryName && isCompositeFundingEpisodeCategory(selectedEpisodeCategoryName)) {
            message += `, voor zorgpad ${translate.episodeCategoryEnum[selectedEpisodeCategoryName]}`;
        }

        if (selectedInsurer?.label) {
            message += `, voor ${selectedInsurer?.label}`;
        }

        message += isPreview ? ', wilt proefexporteren?' : ', wilt exporteren?';

        return message;
    }

    function isCompositeFundingEpisodeCategory(episodeCategory: EpisodeCategory) {
        return compositeFundingEpisodeCategories.some((ec) => ec.name === episodeCategory);
    }
}
